import React, { useState } from 'react';

import { DollarSign, Info, TrendingUp } from 'lucide-react';

import { formatCurrency } from '../../utils/utils';

import type { RevenueAnalysis, ScheduleAFallback } from '../../types';

export const scheduleAFallbackText = (fallback: ScheduleAFallback): string =>
	`Priced on ${fallback.used_config_name ?? 'the default Schedule A'}: ${
		fallback.partner_name ?? 'this partner'
	} has no ${fallback.risk_tier} Schedule A, so the partner split is missing.`;

interface RevenueAnalysisSummaryProps {
	revenueAnalysis: RevenueAnalysis;
	analysisType?: string; // IC_PLUS, TIERED, FLAT_TIERED, etc.
}

const RevenueAnalysisSummary: React.FC<RevenueAnalysisSummaryProps> = ({
	revenueAnalysis,
	analysisType,
}) => {
	const [showDetails, setShowDetails] = useState(false);
	const {
		total_revenue,
		agent_share,
		agent_share_percent,
		breakdown,
		schedule_a_fallback,
	} = revenueAnalysis;
	if (revenueAnalysis.unavailable) {
		return (
			<div
				title={revenueAnalysis.unavailable_reason}
				className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg bg-gray-50 border-gray-200"
			>
				<div className="text-left">
					<div className="text-xs text-gray-600 font-medium">ISO Profit</div>
					<div className="text-base font-bold text-gray-500">Unavailable</div>
				</div>
			</div>
		);
	}

	// `breakdown`/`details`/`network_data` can be partially populated depending
	// on rate-plan type and which networks appear on the statement (e.g. no
	// AMEX/EBT). Treat every nested field as optional so a missing network
	// can't throw and blank the whole analysis page.
	const details = breakdown?.details;
	const networkData = details?.network_data;

	// Detect if this is FLAT_TIERED based on presence of Clay's formula fields
	// or explicitly via analysisType prop
	const isFlatTiered =
		analysisType === 'FLAT_TIERED' ||
		(details?.gross_profit !== undefined && details?.gross_profit !== null);

	// Note: house_share removed from UI per meeting feedback (Clay/John)
	// Data still calculated/stored in backend for future Super Admin roll-up reports

	const formatPercent = (value: number) => `${value.toFixed(2)}%`;
	const formatRate = (value: number) => `${(value * 100).toFixed(2)}%`;

	// Calculate total transaction count from network_data if available, otherwise use legacy field
	const getTotalTransactionCount = (): number => {
		if (networkData) {
			return (
				(networkData.vmd?.transaction_count ?? 0) +
				(networkData.amex?.transaction_count ?? 0) +
				(networkData.pin_debit?.transaction_count ?? 0) +
				(networkData.ebt?.transaction_count ?? 0)
			);
		}
		return details?.total_transaction_count || 0;
	};

	// Calculate total volume from network_data if available, otherwise use legacy field
	const getTotalVolume = (): number => {
		if (networkData) {
			return (
				(networkData.vmd?.volume ?? 0) +
				(networkData.amex?.volume ?? 0) +
				(networkData.pin_debit?.volume ?? 0) +
				(networkData.ebt?.volume ?? 0)
			);
		}
		return details?.total_volume || 0;
	};

	const totalTransactionCount = getTotalTransactionCount();
	const totalVolume = getTotalVolume();

	// Determine if profit is negative for styling
	const isNegative = total_revenue < 0;

	return (
		<div className="relative">
			{/* Compact Summary Button */}
			<button
				type="button"
				onClick={() => setShowDetails(!showDetails)}
				onMouseEnter={() => setShowDetails(true)}
				onMouseLeave={() => setShowDetails(false)}
				className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
					isNegative
						? 'bg-red-50 hover:bg-red-100 border-red-200'
						: 'bg-success-50 hover:bg-success-100 border-success-200'
				}`}
			>
				<div
					className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${
						isNegative ? 'bg-red-600' : 'bg-success-600'
					}`}
				>
					<DollarSign className="w-4 h-4 text-white" />
				</div>
				<div className="text-left">
					<div className="text-xs text-gray-600 font-medium">ISO Profit</div>
					<div
						className={`text-base font-bold ${isNegative ? 'text-red-700' : 'text-success-700'}`}
					>
						{formatCurrency(total_revenue)}
					</div>
				</div>
				<Info className="w-4 h-4 text-gray-400" />
			</button>

			{/* Detailed Popover */}
			{showDetails && (
				<div
					className="absolute top-full left-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
					onMouseEnter={() => setShowDetails(true)}
					onMouseLeave={() => setShowDetails(false)}
				>
					<div className="p-4">
						{/* Header */}
						<div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
							<TrendingUp className="w-5 h-5 text-success-600" />
							<div>
								<h3 className="text-sm font-semibold text-gray-900">
									ISO Profit Analysis
								</h3>
								<p className="text-xs text-gray-500">
									Revenue from Schedule A buy rates
								</p>
							</div>
						</div>

						{/* Revenue Summary Section - Different for FLAT_TIERED vs IC_PLUS */}
						<div className="space-y-3 mb-4">
							{isFlatTiered ? (
								<>
									{/* FLAT_TIERED: Clay's Formula: My Revenue = Gross Profit - Buy Rate Cost */}
									{/* Gross Profit Row */}
									<div className="flex justify-between items-center text-sm">
										<div>
											<span className="text-gray-600">Gross Profit</span>
											<div className="text-xs text-gray-400">
												Merchant Fees − IC − CB
											</div>
										</div>
										<span className="font-semibold text-gray-700">
											{formatCurrency(details?.gross_profit || 0)}
										</span>
									</div>
									{/* Buy Rate Cost Row */}
									<div className="flex justify-between items-center text-sm">
										<div>
											<span className="text-gray-600">Buy Rate Cost</span>
											<div className="text-xs text-gray-400">
												Platform fees to processor
											</div>
										</div>
										<span className="font-semibold text-red-600">
											− {formatCurrency(details?.buy_rate_cost || 0)}
										</span>
									</div>
								</>
							) : (
								<>
									{/* IC_PLUS/TIERED: Margin-based calculation */}
									{/* Volume Margin */}
									<div className="flex justify-between items-center text-sm">
										<div>
											<span className="text-gray-600">Volume Margin</span>
											<div className="text-xs text-gray-400">
												Discount fees − buy rate
											</div>
										</div>
										<span className="font-semibold text-success-600">
											{formatCurrency(breakdown?.volume_margin ?? 0)}
										</span>
									</div>
									{/* Auth Margin */}
									<div className="flex justify-between items-center text-sm">
										<div>
											<span className="text-gray-600">Auth Margin</span>
											<div className="text-xs text-gray-400">
												Auth fees − buy rate
											</div>
										</div>
										<span className="font-semibold text-success-600">
											{formatCurrency(breakdown?.auth_margin ?? 0)}
										</span>
									</div>
									{/* Monthly Margin */}
									<div className="flex justify-between items-center text-sm">
										<div>
											<span className="text-gray-600">Monthly Margin</span>
											<div className="text-xs text-gray-400">
												Fees −{' '}
												{formatCurrency(details?.monthly_buy_costs ?? 0)}{' '}
												costs
											</div>
										</div>
										<span className="font-semibold text-success-600">
											{formatCurrency(breakdown?.monthly_margin ?? 0)}
										</span>
									</div>
								</>
							)}
							{/* Divider */}
							<div className="border-t border-gray-200"></div>
							{/* My Revenue (Net Profit) */}
							<div className="flex justify-between items-center">
								<div>
									<span className="font-semibold text-gray-900">
										My Revenue
									</span>
									<div className="text-xs text-gray-400">
										Net profit after all costs
									</div>
								</div>
								<span
									className={`text-xl font-bold ${total_revenue >= 0 ? 'text-success-700' : 'text-red-600'}`}
								>
									{formatCurrency(total_revenue)}
								</span>
							</div>
							{/* Agent Share (if split applies) */}
							{agent_share_percent < 100 && (
								<div className="flex justify-between items-center text-sm bg-gray-50 rounded px-2 py-1">
									<span className="text-gray-600">
										Your Share ({formatPercent(agent_share_percent)})
									</span>
									<span className="font-semibold text-primary-700">
										{formatCurrency(agent_share)}
									</span>
								</div>
							)}
						</div>

						{/* Profit Breakdown - Only show for FLAT_TIERED since IC_PLUS already shows margins above */}
						{isFlatTiered && (
							<div className="border-t border-gray-200 pt-3">
								<div className="text-xs font-semibold text-gray-700 mb-2">
									Profit Breakdown
								</div>
								<div className="space-y-2">
									{/* Volume Margin */}
									<div className="flex justify-between items-center text-xs">
										<div className="text-gray-600">
											<span className="font-medium">Volume Margin</span>
											<div className="text-gray-400">
												{formatCurrency(totalVolume)} volume
												{networkData ? (
													<span> (VMD + Amex)</span>
												) : details?.discount_buy_rate ? (
													<span>
														{' '}
														× {formatRate(details.discount_buy_rate)}
													</span>
												) : null}
											</div>
										</div>
										<span className="font-semibold text-success-600">
											{formatCurrency(breakdown?.volume_margin ?? 0)}
										</span>
									</div>

									{/* Auth Margin */}
									<div className="flex justify-between items-center text-xs">
										<div className="text-gray-600">
											<span className="font-medium">Auth Margin</span>
											<div className="text-gray-400">
												{totalTransactionCount.toLocaleString()} transactions
												{networkData ? (
													<span> (All types)</span>
												) : details?.auth_buy_rate ? (
													<span>
														{' '}
														× {formatCurrency(details.auth_buy_rate)}
													</span>
												) : null}
											</div>
										</div>
										<span className="font-semibold text-success-600">
											{formatCurrency(breakdown?.auth_margin ?? 0)}
										</span>
									</div>

									{/* Monthly Margin */}
									<div className="flex justify-between items-center text-xs">
										<div className="text-gray-600">
											<span className="font-medium">Monthly Fee Margin</span>
											<div className="text-gray-400">
												Fees minus{' '}
												{formatCurrency(details?.monthly_buy_costs ?? 0)}{' '}
												costs
											</div>
										</div>
										<span className="font-semibold text-success-600">
											{formatCurrency(breakdown?.monthly_margin ?? 0)}
										</span>
									</div>

									{/* Batch Margin (NEW - only show if present) */}
									{breakdown?.batch_margin !== undefined &&
										breakdown?.batch_margin !== 0 && (
											<div className="flex justify-between items-center text-xs">
												<div className="text-gray-600">
													<span className="font-medium">Batch Margin</span>
													<div className="text-gray-400">
														Batch fees (
														{formatCurrency(
															details?.batch_fee_rate || 0,
														)}{' '}
														× 30 days)
													</div>
												</div>
												<span className="font-semibold text-success-600">
													{formatCurrency(breakdown?.batch_margin ?? 0)}
												</span>
											</div>
										)}
								</div>
							</div>
						)}
					</div>
				</div>
			)}
			{schedule_a_fallback && (
				<p
					role="status"
					className="mt-1 max-w-xs text-xs text-sand-900"
					data-testid="schedule-a-fallback"
				>
					{scheduleAFallbackText(schedule_a_fallback)}
				</p>
			)}
		</div>
	);
};

export default RevenueAnalysisSummary;
